import type { RouteLocationRaw } from 'vue-router'
import type { BaseAuthorWork } from '~/utils/solr/author-work'
import type { BaseJournal } from '~/utils/solr/journal'
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import type { BookAndArticle } from '~/utils/solr/literature'
import type { NewestDoc } from '~/utils/solr/newest'
import type { SearchResult } from '~/utils/solr/search'
import type { GlobalSearch } from './solr-search'
import { parseJournalIssueFromId } from '~~/server/utils/solr/journal/volume-issue'
import { extractName, normalizeFacetName } from './solr-facet'
import { FILTER_FACET_TAGS } from './search'

export { default as resolveSolrDocumentTitle } from './solr-title'

type SolrDocument = BookAndArticle | BaseJournal | LawAndCommentary | GlobalSearch | NewestDoc | BaseAuthorWork

/**
 * Extracts the publication date from a Solr document based on its nodeType.
 */
export function getSolrDocumentDate(doc: SolrDocument | null | undefined): string | null {
  if (!doc) {
    return null
  }

  switch (doc.nodeType) {
    case 'legal_commentary':
      if ('originalPublicationDate' in doc && doc.originalPublicationDate) {
        return doc.originalPublicationDate
      }

      if ('lastUpdatedByEditor' in doc && doc.lastUpdatedByEditor) {
        return Array.isArray(doc.lastUpdatedByEditor) ? doc.lastUpdatedByEditor[0] : doc.lastUpdatedByEditor
      }
      return null

    case 'academic_article':
      if ('lastUpdatedByEditor' in doc && doc.lastUpdatedByEditor) {
        return Array.isArray(doc.lastUpdatedByEditor) ? doc.lastUpdatedByEditor[0] : doc.lastUpdatedByEditor
      }
      return null

    case 'book':
    case 'news_notice':
      if ('dateOfPublication' in doc && doc.dateOfPublication) {
        return doc.dateOfPublication
      }

      if ('year' in doc && doc.year) {
        return new Date(doc.year).toISOString()
      }
      return null

    case 'journal_article':
    case 'journal_editorial':
      if ('publish_date' in doc && doc.publish_date) {
        return doc.publish_date
      }
      return null

    case 'law':
      if ('lastChangeInForce' in doc && doc.lastChangeInForce) {
        return doc.lastChangeInForce
      }
      if ('dateInForce' in doc && doc.dateInForce) {
        return doc.dateInForce
      }
      return null

    case 'author':
      return null

    default:
      return null
  }
}

/**
 * Extracts the author from a Solr document based on its nodeType.
 */
export function getSolrDocumentAuthor(doc: SolrDocument | null | undefined): string | undefined {
  if (!doc) {
    return undefined
  }

  if (doc.nodeType === 'law') {
    return 'Lovdata'
  }

  const author = 'author' in doc && Array.isArray(doc.author) ? doc.author : []
  const authorList = 'author_list' in doc && Array.isArray(doc.author_list) ? doc.author_list : []

  if (author.length > 0) {
    const byline = author.map(extractName).filter(Boolean).join(', ')
    return hasAdditionalCollaborators(author, authorList) ? `${byline} m.fl.` : byline
  }

  if (authorList.length > 0) {
    return authorList
      .map(extractName)
      .filter(Boolean)
      .join(', ')
  }

  if ('authorName' in doc && doc.authorName) {
    return Array.isArray(doc.authorName) ? doc.authorName.join(', ') : doc.authorName
  }
}

function hasAdditionalCollaborators(author: string[], authorList: string[]): boolean {
  if (authorList.length === 0) {
    return false
  }

  const bylineNames = new Set(author.map(name => normalizeFacetName(extractName(name))))
  return authorList.some(entry => !bylineNames.has(normalizeFacetName(extractName(entry))))
}

export function resolveSolrDocumentUrl(doc: SolrDocument | SearchResult | null | undefined, searchQuery?: string): RouteLocationRaw | undefined {
  if (!doc) {
    return undefined
  }

  const q = searchQuery?.trim()

  if (doc.nodeType === 'law' && 'id' in doc) {
    const lawdocId = doc.id
    if (!lawdocId) {
      return undefined
    }

    const lovId = (lawdocId ? lawdocId.split('/').filter(Boolean) : [])

    return {
      name: 'law-by-id',
      params: { lovId },
      ...(q && { query: { lcq: q } }),
    } satisfies RouteLocationRaw
  }

  if (doc.nodeType === 'legal_commentary' && ('lawId' in doc || 'lawFragmentId' in doc)) {
    // For some reason TypeScript cannot infer that legal_commentary has lawId and lawFragmentId
    const commentary = doc as LawAndCommentary | GlobalSearch
    if (!commentary.lawId) {
      return undefined
    }
    const lovId = [
      commentary.lawId,
      ...(commentary.lawFragmentId ? commentary.lawFragmentId.split('/').filter(Boolean) : []),
    ]

    return {
      name: 'law-by-id',
      params: { lovId },
      ...(q && { query: { lcq: q } }),
    } satisfies RouteLocationRaw
  }

  if (doc.nodeType === 'book' && 'id' in doc) {
    const bookdocId = doc.id
    if (!bookdocId) {
      return undefined
    }
    const bokId = bookdocId.startsWith('isbn-')
      ? bookdocId.split('/').filter(Boolean)
      : [bookdocId]

    return {
      name: 'book-by-id',
      params: { bokId },
      ...(q && { query: { q } }),
    } as unknown as RouteLocationRaw
  }

  if ((doc.nodeType === 'journal') && 'publisherId' in doc) {
    if (!doc.publisherId) {
      return undefined
    }

    return {
      name: 'journal-publication',
      params: { publisherId: [doc.publisherId] },
      ...(q && { query: { q } }),
    } as unknown as RouteLocationRaw
  }

  if ((doc.nodeType === 'journal_article' || doc.nodeType === 'journal_editorial')
    && 'publisherId' in doc && 'id' in doc) {
    const id = doc.id
    const publisherId = doc.publisherId

    if (!publisherId || !id) {
      return undefined
    }

    let volume: string | undefined | null
    let issue: string | undefined | null

    if ('volume' in doc && doc.volume && 'issue' in doc && doc.issue) {
      volume = doc.volume
      issue = doc.issue
    }
    else {
      const parsed = parseJournalIssueFromId(id)
      volume = parsed?.volume
      issue = parsed?.issue
    }

    if (!volume || !issue) {
      return undefined
    }

    // Strip section suffix if the group head is a journal_section (child ID preserved after collapse)
    const resolvedId = id.replace(/\/(?:section|seksjon)-\d+$/, '')
    const articleNumber = resolvedId.split('/').at(-1)
    if (!articleNumber) {
      return undefined
    }

    return {
      name: 'journal-article-volume',
      params: {
        publisherId,
        volume,
        issue: issue.startsWith('utgave-') ? issue.split('-')[1]! : issue,
        articleNumber,
      },
      ...(q && { query: { q } }),
    } as unknown as RouteLocationRaw
  }

  if (doc.nodeType === 'academic_article') {
    const article = doc
    const id = article.remoteId || article.id
    if (!id) {
      return undefined
    }
    return {
      name: 'academic-article-by-id',
      params: { artikkelId: [id] },
      ...(q && { query: { q } }),
    } as unknown as RouteLocationRaw
  }

  if (doc.nodeType === 'author') {
    const author = doc
    if (!author.remoteId) {
      return undefined
    }
    return {
      name: 'author',
      params: { slug: author.remoteId },
    }
  }
}

/**
 * Converts a document's publication date to a Date object for sorting.
 * Uses getSolrDocumentDate internally for consistent field selection.
 */
export function getDocumentSortDate(doc: SolrDocument): Date {
  return new Date(getSolrDocumentDate(doc) || 0)
}

/**
 * Temporary maps Solr news notice type codes to human-readable labels. (will be replaced with actual labels from Solr later)
 */
export function noticeTypeToLabel(noticeType: string | null | undefined): string {
  switch (noticeType) {
    case '0':
      return 'Lovkommentar'
    case '1':
      return 'Fagbok'
    case '2':
      return 'Fagartikkel'
    case '3':
      return 'Tidsskrift'
    default:
      return 'Nyhetsmelding'
  }
}

/**
 * Create a tagged filter query for Solr
 * @example tagFilter('nodeType', 'nodeType:(book OR chapter)') => '{!tag=nt}nodeType:(book OR chapter)'
 */
export function tagFilter(tag: keyof typeof FILTER_FACET_TAGS, filter: string): string {
  return `{!tag=${FILTER_FACET_TAGS[tag]}}${filter}`
}

/**
 * Create an excluded facet field for multi-select faceting
 * @example excludeFromFacet('nodeType', 'nodeType') => '{!ex=nt}nodeType'
 */
export function excludeFromFacet(tag: keyof typeof FILTER_FACET_TAGS, field: string): string {
  return `{!ex=${FILTER_FACET_TAGS[tag]}}${field}`
}
